import React from 'react';
import { TrendingUp, Repeat, AlertTriangle, AlertCircle, Bell } from 'lucide-react';

/**
 * Derived "signal flag" vocabulary, mirrored from the reference design.
 * Backend has no `flags` column — these are computed client-side.
 */
export function deriveFlags(c) {
  const flags = [];
  if (!c) return flags;
  if (typeof c.contractValue === 'number' && c.contractValue > 500000) flags.push('high-value');
  if (c.renewalType === 'auto_renew') flags.push('auto-renew');
  if (Array.isArray(c.keyRisks) && c.keyRisks.length > 0) flags.push('risk');
  // Active with no executed date: "Unsigned" only when there is no executed
  // copy either. An executed file on record means it IS signed and just the
  // date is missing — a data gap, not a signature problem.
  if (c.status === 'active' && !c.executedDate) {
    flags.push(c.executedFileId ? 'no-executed-date' : 'unsigned-addendum');
  }
  return flags;
}

export const FLAG_META = {
  'high-value':        { tone: 'purple', label: 'High value',    Icon: TrendingUp,    tip: 'High value (> $500K)' },
  'auto-renew':        { tone: 'blue',   label: 'Auto-renew',    Icon: Repeat,        tip: 'Auto-renew clause' },
  'risk':              { tone: 'yellow', label: 'Risk flagged',  Icon: AlertTriangle, tip: 'Flagged risk' },
  'unsigned-addendum': { tone: 'red',    label: 'Unsigned',      Icon: AlertCircle,   tip: 'Active but no executed copy or executed date' },
  'no-executed-date':  { tone: 'yellow', label: 'No signed date', Icon: AlertCircle,  tip: 'Executed copy on file, but the executed date is not set' },
  'expiring':          { tone: 'red',    label: 'Expiring soon', Icon: Bell,          tip: 'Expiring within 30 days' },
};

/**
 * Small cluster of tonal signal badges for table rows. Includes an auto-derived
 * "expiring" flag when the contract expires within 30 days.
 * Always reserves a fixed width so rows stay aligned.
 */
export default function SignalStrip({ contract, flags: extraFlags, max = 3 }) {
  const derived = deriveFlags(contract);
  const all = [...derived, ...(extraFlags || [])];

  if (contract && contract.expirationDate) {
    const now = new Date();
    const exp = new Date(contract.expirationDate);
    const d = Math.round((exp - now) / 86400000);
    // Skip the "expiring soon" flag when legal has dismissed the
    // renewal reminder for this exact expiration_date. PUT /contracts
    // clears the dismissal if the date later shifts, so this rearms
    // automatically on extension/renewal.
    const renewalDismissed =
      !!contract.renewalDismissedForDate &&
      contract.renewalDismissedForDate === contract.expirationDate;
    if (d > 0 && d <= 30 && !renewalDismissed) all.push('expiring');
  }

  if (!all.length) {
    return <span style={{ display: 'inline-block', width: 18, height: 18 }} />;
  }

  return (
    <span className="row" style={{ gap: 3 }}>
      {all.slice(0, max).map((f, i) => {
        const m = FLAG_META[f];
        if (!m) return null;
        const IconComp = m.Icon;
        return (
          <span key={`${f}-${i}`} className={`signal ${m.tone}`} title={m.tip}>
            <IconComp className="w-[11px] h-[11px]" />
          </span>
        );
      })}
    </span>
  );
}
